'use client'

import { useState, useEffect, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { ChevronDown, ChevronUp, Search, Filter } from 'lucide-react'

interface FAQ {
  _id: string
  question: string
  answer: string
  category: string
  order: number
  tags: string[]
}

interface DynamicFAQProps {
  title?: string
  subtitle?: string
  showFilters?: boolean
  showSearch?: boolean
  category?: string
  limit?: number
  className?: string
}

export default function DynamicFAQ({
  title = 'Frequently Asked Questions',
  subtitle = 'Find answers to commonly asked questions',
  showFilters = true,
  showSearch = true,
  category,
  limit,
  className = ''
}: DynamicFAQProps) {
  const [faqs, setFaqs] = useState<FAQ[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [openIndex, setOpenIndex] = useState<number | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCategory, setSelectedCategory] = useState(category || 'all')
  const [sessionId] = useState(() => Math.random().toString(36).substring(7))
  const searchTimeoutRef = useRef<NodeJS.Timeout>()
  const expandTimeRef = useRef<number>()

  const categories = [
    { value: 'all', label: 'All Categories' },
    { value: 'general', label: 'General' },
    { value: 'services', label: 'Services' },
    { value: 'pricing', label: 'Pricing' },
    { value: 'support', label: 'Support' },
    { value: 'technical', label: 'Technical' }
  ]

  useEffect(() => {
    fetchFAQs()
  }, [searchTerm, selectedCategory, category, limit])

  const fetchFAQs = async () => {
    try {
      setLoading(true)
      const params = new URLSearchParams({
        publicOnly: 'true', // Only fetch active FAQs
        sortBy: 'order',
        sortOrder: 'asc',
        ...(limit && { limit: limit.toString() }),
        ...(searchTerm && { search: searchTerm }),
        ...(selectedCategory !== 'all' && { category: selectedCategory }),
        ...(category && { category }) // Override if category prop is provided
      })

      const response = await fetch(`/api/faqs?${params}`)
      if (!response.ok) {
        throw new Error('Failed to fetch FAQs')
      }
      
      const data = await response.json()
      setFaqs(data.faqs)

      // Track FAQ view analytics
      if (data.faqs.length > 0) {
        trackAnalytics('view', data.faqs[0]._id)
      }
    } catch (error) {
      console.error('Error fetching FAQs:', error)
      setError('Failed to load FAQs')
    } finally {
      setLoading(false)
    }
  }

  const trackAnalytics = async (action: string, faqId?: string, additionalData?: any) => {
    try {
      await fetch('/api/faq-analytics', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          faqId: faqId || (faqs.length > 0 ? faqs[0]._id : null),
          action,
          sessionId,
          searchQuery: action === 'search' ? searchTerm : undefined,
          filterCategory: action === 'filter' ? selectedCategory : undefined,
          source: 'contact-page',
          referrer: document.referrer || undefined,
          ...additionalData
        })
      })
    } catch (error) {
      // Silently fail analytics to not interrupt user experience
      console.debug('Analytics tracking failed:', error)
    }
  }

  const toggleFAQ = (index: number) => {
    const wasOpen = openIndex === index
    
    // Track time spent if closing an FAQ
    if (wasOpen && expandTimeRef.current) {
      const timeSpent = Date.now() - expandTimeRef.current
      trackAnalytics('expand', faqs[index]._id, { timeSpent })
    }
    
    setOpenIndex(openIndex === index ? null : index)
    
    // Track expand analytics and set start time
    if (!wasOpen) {
      trackAnalytics('expand', faqs[index]._id)
      expandTimeRef.current = Date.now()
    }
  }

  // Handle search with debouncing and analytics
  const handleSearch = (value: string) => {
    setSearchTerm(value)
    
    // Clear existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
    }
    
    // Track search with debouncing
    if (value.trim()) {
      searchTimeoutRef.current = setTimeout(() => {
        trackAnalytics('search', undefined, { searchQuery: value })
      }, 1000)
    }
  }

  // Handle category filter with analytics
  const handleCategoryChange = (newCategory: string) => {
    setSelectedCategory(newCategory)
    trackAnalytics('filter', undefined, { filterCategory: newCategory })
  }

  if (loading) {
    return (
      <div className={`py-20 bg-gray-50 ${className}`}>
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <div className="animate-pulse">
              <div className="h-8 bg-gray-200 rounded-lg w-64 mx-auto mb-4"></div>
              <div className="h-4 bg-gray-200 rounded-lg w-96 mx-auto mb-12"></div>
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-16 bg-gray-200 rounded-lg"></div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className={`py-20 bg-gray-50 ${className}`}>
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <div className="text-red-600 mb-4">⚠️ {error}</div>
            <button
              onClick={fetchFAQs}
              className="text-blue-600 hover:text-blue-700 font-medium"
            >
              Try again
            </button>
          </div>
        </div>
      </div>
    )
  }

  if (faqs.length === 0) {
    return (
      <div className={`py-20 bg-gray-50 ${className}`}>
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              {title}
            </h2>
            <p className="text-gray-600 mb-8">
              No FAQs available at the moment. Please check back later.
            </p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <section className={`py-20 bg-gray-50 ${className}`}>
      <div className="container mx-auto px-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              {title}
            </h2>
            <p className="text-xl text-gray-600">
              {subtitle}
            </p>
          </div>

          {/* Search and Filters */}
          {(showSearch || showFilters) && (
            <div className="mb-8 space-y-4">
              {showSearch && (
                <div className="relative max-w-md mx-auto">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                  <input
                    type="text"
                    placeholder="Search FAQs..."
                    value={searchTerm}
                    onChange={(e) => handleSearch(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              )}
              
              {showFilters && !category && (
                <div className="flex justify-center">
                  <select
                    value={selectedCategory}
                    onChange={(e) => handleCategoryChange(e.target.value)}
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    {categories.map(cat => (
                      <option key={cat.value} value={cat.value}>
                        {cat.label}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          )}

          {/* FAQ List */}
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <motion.div
                key={faq._id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden"
              >
                <button
                  onClick={() => toggleFAQ(index)}
                  className="w-full px-6 py-4 text-left flex justify-between items-center hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-inset"
                >
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 pr-4">
                      {faq.question}
                    </h3>
                    {faq.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {faq.tags.slice(0, 3).map((tag, tagIndex) => (
                          <span
                            key={tagIndex}
                            className="px-2 py-1 text-xs bg-blue-100 text-blue-600 rounded"
                          >
                            #{tag}
                          </span>
                        ))}
                        {faq.tags.length > 3 && (
                          <span className="text-xs text-gray-500">
                            +{faq.tags.length - 3} more
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="flex-shrink-0 ml-4">
                    {openIndex === index ? (
                      <ChevronUp className="w-5 h-5 text-gray-500" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-500" />
                    )}
                  </div>
                </button>
                
                <AnimatePresence>
                  {openIndex === index && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3, ease: 'easeInOut' }}
                      className="border-t border-gray-200"
                    >
                      <div className="px-6 py-4">
                        <div className="prose prose-gray max-w-none">
                          <p className="text-gray-600 leading-relaxed whitespace-pre-line">
                            {faq.answer}
                          </p>
                        </div>
                        
                        <div className="mt-4 flex items-center justify-between">
                          <span className="text-xs text-blue-600 bg-blue-50 px-2 py-1 rounded capitalize">
                            {faq.category}
                          </span>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>

          {/* Show message if no results from search/filter */}
          {faqs.length === 0 && (searchTerm || selectedCategory !== 'all') && (
            <div className="text-center py-8">
              <p className="text-gray-500">
                No FAQs found matching your criteria. Try adjusting your search or filter.
              </p>
              <button
                onClick={() => {
                  setSearchTerm('')
                  setSelectedCategory('all')
                  trackAnalytics('filter', undefined, { filterCategory: 'all' })
                }}
                className="mt-2 text-blue-600 hover:text-blue-700 font-medium"
              >
                Clear filters
              </button>
            </div>
          )}
        </div>
      </div>
    </section>
  )
}